import {
  TrendingUp, Lightbulb, Heart, Leaf, Shield, Zap, Crown, Sparkles, Rocket, Smile, Compass, Flame,
  Palette, HandHeart, Scale, Wrench, Target, Globe, type LucideIcon,
} from "lucide-react";

export const TITLE_COLOR = "#7B6FF0"; // ernest purple — titles, eyebrow icons, value pictos
const VALUE_COLORS = ["#E2725B", "#E89B85", "#DE9D8D"]; // primary → tertiary (terracotta family)
const VALUE_FALLBACK = "#D89A88";
export const FEMALE_COLOR = "#E94B7A"; // rose
export const MALE_COLOR = "#2E8B8B"; // deep teal
export const AGE_COLOR = "#3B82A8"; // slate blue

// ─── Sub-components ───────────────────────────────────────────────────────

export const Chip = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <span className={`inline-flex items-center h-7 px-3.5 rounded-full text-[13px] font-medium bg-card text-foreground/85 border border-border whitespace-nowrap ${className}`}>
    {children}
  </span>
);

export const Eyebrow = ({
  icon: Icon,
  children,
}: {
  icon?: LucideIcon;
  children: React.ReactNode;
}) => (
  <div
    className="flex items-center gap-2 uppercase tracking-[0.08em] text-[13.5px] font-bold"
    style={{ color: TITLE_COLOR }}
  >
    {Icon && <Icon size={15} strokeWidth={2.25} />}
    <span>{children}</span>
  </div>
);

export const Empty = ({ children }: { children: React.ReactNode }) => (
  <span className="text-sm text-muted-foreground italic">{children}</span>
);

// ─── Value bar row ────────────────────────────────────────────────────────

// Map common value keywords → meaningful icon. Falls back to Sparkles.
const VALUE_ICON_RULES: { match: RegExp; Icon: LucideIcon }[] = [
  { match: /perform|result|excellen|effic/i, Icon: TrendingUp },
  { match: /ambit|growth|bold|drive|aspir/i, Icon: Rocket },
  { match: /innov|creativ|imagin|origin/i, Icon: Lightbulb },
  { match: /design|aesthet|art|style|craft/i, Icon: Palette },
  { match: /craftsman|quality|premium|luxur/i, Icon: Crown },
  { match: /sustain|eco|green|natur|plant/i, Icon: Leaf },
  { match: /trust|integri|reliab|safe|secur/i, Icon: Shield },
  { match: /communit|together|inclus|belong|collab/i, Icon: HandHeart },
  { match: /love|care|warm|empath|kind/i, Icon: Heart },
  { match: /fun|joy|happy|playful|smile/i, Icon: Smile },
  { match: /energy|power|bold|vital|dynam/i, Icon: Zap },
  { match: /passion|intens|fire|hot/i, Icon: Flame },
  { match: /explor|discover|advent|journey|curio/i, Icon: Compass },
  { match: /global|world|internation|reach/i, Icon: Globe },
  { match: /fair|equit|balance|honest|ethic/i, Icon: Scale },
  { match: /tech|engineer|build|product|tool/i, Icon: Wrench },
  { match: /focus|precis|target|sharp/i, Icon: Target },
];

function iconForValue(value: string): LucideIcon {
  for (const rule of VALUE_ICON_RULES) {
    if (rule.match.test(value)) return rule.Icon;
  }
  return Sparkles;
}

const RANK_META: Record<number, { label: string }> = {
  1: { label: "Primary" },
  2: { label: "Secondary" },
  3: { label: "Tertiary" },
};

// Width scales by how many values exist + this row's position, so a single
// value still fills the bar and two values read as primary/secondary cleanly.
function valueBarWidth(index: number, total: number): string {
  if (total === 1) return "82%";
  if (total === 2) return index === 0 ? "82%" : "52%";
  // 3+
  const scale = ["82%", "55%", "32%"];
  return scale[index] ?? "20%";
}

function ordinalLabel(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export const ValueBar = ({
  value,
  rank,
  index,
  total,
}: {
  value: string;
  rank: number;
  index: number;
  total: number;
}) => {
  const label = RANK_META[rank]?.label ?? `${ordinalLabel(index + 1)}`;
  const color = VALUE_COLORS[index] ?? VALUE_FALLBACK;
  const Icon = iconForValue(value);
  void total;
  return (
    <div className="flex items-center gap-3 h-[46px]">
      <Icon size={15} strokeWidth={1.9} className="text-foreground shrink-0" />
      <span className="flex-1 min-w-0 text-[15px] font-medium text-foreground truncate">
        {value}
      </span>
      <span
        className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.1em] whitespace-nowrap"
        style={{ backgroundColor: `${color}22`, color }}
      >
        {label}
      </span>
    </div>
  );
};

// ─── Gender split bar ─────────────────────────────────────────────────────


export const GenderBar = ({
  split,
}: {
  split: { female: number; male: number; label: string };
}) => (
  <div>
    <div className="flex h-2 rounded-full overflow-hidden">
      <div style={{ width: `${split.female}%`, backgroundColor: FEMALE_COLOR }} />
      <div style={{ width: `${split.male}%`, backgroundColor: MALE_COLOR }} />
    </div>
    <div className="mt-2.5 flex items-center justify-between text-[12px] text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: FEMALE_COLOR }} />
        Women <span className="font-medium text-foreground tabular-nums">{split.female}%</span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: MALE_COLOR }} />
        Men <span className="font-medium text-foreground tabular-nums">{split.male}%</span>
      </span>
    </div>
  </div>
);

// ─── Age range bar ────────────────────────────────────────────────────────

const AGE_MIN = 18;
const AGE_MAX = 70;
const AGE_TICKS = [18, 30, 45, 60, 70];

export const AgeRangeBar = ({ range, compact = false }: { range: [number, number]; compact?: boolean }) => {
  const [lo, hi] = range;
  const cLo = Math.max(AGE_MIN, Math.min(AGE_MAX, lo));
  const cHi = Math.max(AGE_MIN, Math.min(AGE_MAX, hi));
  const span = AGE_MAX - AGE_MIN;
  const left = ((cLo - AGE_MIN) / span) * 100;
  const width = Math.max(2, ((cHi - cLo) / span) * 100);
  const rangeLabel =
    cLo === cHi ? `~${cLo} yrs` : `${cLo}–${cHi === AGE_MAX ? "70+" : cHi} yrs`;
  return (
    <div>
      <div className="relative h-2 rounded-full bg-muted">
        <div
          className="absolute top-0 h-full rounded-full transition-all duration-500"
          style={{ left: `${left}%`, width: `${width}%`, backgroundColor: AGE_COLOR }}
        />
        <div
          className="absolute -top-0.5 w-3 h-3 rounded-full border-2 border-background"
          style={{ left: `calc(${left}% - 6px)`, backgroundColor: AGE_COLOR }}
        />
        <div
          className="absolute -top-0.5 w-3 h-3 rounded-full border-2 border-background"
          style={{ left: `calc(${left + width}% - 6px)`, backgroundColor: AGE_COLOR }}
        />
      </div>
      <div className="mt-2 flex justify-between text-[12px] text-muted-foreground tabular-nums">
        {AGE_TICKS.map((t) => (
          <span key={t}>{t === AGE_MAX ? `${t}+` : t}</span>
        ))}
      </div>
      {!compact && (
        <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted/60 text-[11px] font-medium text-foreground/80">
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: AGE_COLOR }} />
          {rangeLabel} targeted
        </div>
      )}
    </div>
  );
};

